import React, { useState, useEffect } from 'react';
import { Alert, Box, TextField, Button, TableContainer, Table, TableHead, TableCell, TableRow, Paper, TableBody, FormControl, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getBiocharMc, postBiocharMc } from '../../../../../actions/tigercat-burns/tigercat-actions';
import Paginator from '../../../../layout/Pagination';
import useTsPaginator from 'ts-paginator';
import { getTableTotalRecordCount } from '../../../../../actions/tigercat-burns/tigercat-base-actions';

const BiocharMcBags = ({ projectId }) => {
    const [biocharMcState, setBiocharMcState] = useState([]);
    const dispatch = useDispatch();
    const biocharMoistureContentBags = useSelector(state => state.tigercatBurnsReducer.biocharMoistureContents.data);
    const [loading, setLoading] = useState(false);
    const tableRecordCount = useSelector(state => state.tigercatBurnsBaseReducer.totalRecordCount.count)
    const [currentProjectId, setCurrentProjectId] = useState(projectId);
    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    // useEffect(() => {
    //     dispatch(getBiocharMc(projectId));
    // }, [dispatch, projectId]);


    useEffect(() => {
        // if (currentProjectId !== projectId) {
        setCurrentProjectId(projectId);
        _handleChangePage(0); // Reset to the first page
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount); // Reset to the first page
        // }
    }, [projectId, currentProjectId, tableRecordCount]);

    useEffect(() => {
        dispatch(getTableTotalRecordCount('biochar_mc_bags', projectId));
    }, [dispatch, projectId]);

    useEffect(() => {
        if (!isNaN(currentPage) && !isNaN(rowsPerPage)) {
            dispatch(getBiocharMc(projectId, +rowsPerPage, +currentPage));
        }
    }, [dispatch, projectId, rowsPerPage, currentPage]);

    useEffect(() => {
        const stateArray = biocharMoistureContentBags?.map((item) => ({
            id: item.id,
            mbId: item.mbId,
            date: item.date,
            mcSamples: item.mcSamples,
            mcAverage: item.mcAverage,
            dirty: false,
            disabled: item.disabled ?? false
        })) || [];
        setBiocharMcState(stateArray);
    }, [biocharMoistureContentBags]);

    const handleDateSelection = (date, dataId) => {
        setBiocharMcState((prevState) =>
            prevState.map((item) =>
                item.id === dataId
                    ? { ...item, date: moment(date).format('YYYY-MM-DD'), dirty: true }
                    : item
            )
        );
    };

    const handleMcChange = (e, dataId) => {
        const value = e.target.value === '' ? '' : e.target.value;
        setBiocharMcState((prevState) =>
            prevState.map((item) =>
                item.id === dataId
                    ? { ...item, mcSamples: value, dirty: true }
                    : item
            )
        );
    };

    const handleMcAverageChange = (e, dataId) => {
        const value = e.target.value === '' ? '' : e.target.value;
        setBiocharMcState((prevState) =>
            prevState.map((item) =>
                item.id === dataId
                    ? { ...item, mcAverage: value, dirty: true }
                    : item
            )
        );
    };

    const handleSubmitData = async () => {
        try {
            setLoading(true);

            const validBiocharMcState = biocharMcState.filter((i) => {
                if (!i.dirty) return false; // Only submit dirty rows

                // Your current check uses string 'null'. Here we handle real null/undefined/'' too:
                if (!i.date || i.mcSamples === '' || i.mcAverage === '' || i.mcSamples == null || i.mcAverage == null) {
                    return false;
                }

                return true;
            });

            if (validBiocharMcState.length === 0) {
                console.log('No dirty/valid rows to submit');
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('data', JSON.stringify(validBiocharMcState));

            // console.log('Submitting biochar MC data:', validBiocharMcState);

            await dispatch(postBiocharMc(projectId, formData, 'bags'));

            // If we reach here, request completed successfully
            await dispatch(getBiocharMc(projectId, +rowsPerPage, +currentPage));
        } catch (err) {
            console.error('Error submitting biochar MC data', err);
            // Optionally show a toast / alert here
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h2>Biochar MC - Bags</h2>
            <TableContainer key={0} component={Paper}>
                {/* {console.log(biocharMcState)} */}
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell size="small" align="left">MB ID</TableCell>
                            <TableCell size="small" align="left">Date</TableCell>
                            <TableCell size="small" align="left">Number of Moisture Content samples in MB</TableCell>
                            <TableCell size="small" align="left">Moisture Content Average (%)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {biocharMcState?.map((data, index) => (
                            <TableRow key={index}>
                                <TableCell size="small" align="left">{data.mbId}</TableCell>
                                <TableCell size="small" align="left">
                                    <FormControl>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                name={'date'}
                                                value={data.date ? moment(data.date, 'YYYY-MM-DD') : null}
                                                onChange={(e) => handleDateSelection(e.toDate(), data.id)}
                                                disabled={data.disabled}
                                                format="MM/DD/YYYY"
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </TableCell>
                                <TableCell size="small" align="left">
                                    <TextField
                                        name='moisture content samples in mb'
                                        value={data.mcSamples === 'null' ? '' : data.mcSamples}
                                        onChange={(e) => handleMcChange(e, data.id)}
                                        type="text"
                                        disabled={data.disabled}
                                    />
                                </TableCell>
                                <TableCell size="small" align="left">
                                    <TextField
                                        name='moisture content average'
                                        value={data.mcAverage === 'null' ? '' : data.mcAverage}
                                        onChange={(e) => handleMcAverageChange(e, data.id)}
                                        type="text"
                                        disabled={data.disabled}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Paginator
                totalRecordCount={totalRecordCount}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                handleChangePage={_handleChangePage}
                handleChangeRowsPerPage={_handleChangeRowsPerPage}
                determinePaginationMessage={_determinePaginationMessage}
                determinePaginationDisabledState={_determinePaginationDisabledState}
                determineRowsPerPageOptions={_determineRowsPerPageOptions}
            />
            <Box sx={{ position: 'relative', mb: 10 }}>
                {loading ? <CircularProgress sx={{ position: 'absolute', left: '125px', top: '12px' }} /> : <Button variant='contained' sx={{ height: 'auto', mt: 2 }} onClick={handleSubmitData}>Submit Data</Button>}
                {loading && <Alert severity='info' sx={{ my: 2 }}>Please be patient during data submission as file uploads can take some time to complete</Alert>}
            </Box>
        </>
    );
};

export default BiocharMcBags;